import {subWeeks} from "date-fns";
import {useEffect, useRef, useState} from "react";
import ReportUtils from "../../../utils/report-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Filters from "./components/filters.jsx";
import KmCheckService from "../../../services/reports/km-check-service.js";
import Content from "./components/content.jsx";

const initialFilter = () => ({
    dtStart:  subWeeks(new Date(), 1),
    dtEnd: new Date(),
});

const filterLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
};

export default function KmCheck() {
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [filters, setFilters] = useState(initialFilter);
    const [reportData, setReportData] = useState(null);
    const [headerSelected, setHeaderSelected] = useState(-1);
    const title = "Verificação de km inconsistente";
    const printComponentRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
            }, filterLabels)
        );
    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            },
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, null, null);

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setIsLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
            }, filterLabels, true);

            const response = await KmCheckService.apply({ ...formattedFilters, filters: auditFilters});
            setReportData(response.data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        }  catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar relatório.");
            console.error("Erro ao gerar relatório", error);
        } finally {
            setIsLoading(false);
        }
    };

    const clearSearch = () => {
        setFilters(initialFilter());
        setReportData(null);
    };

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        };
        setCollapsed(prev => !prev);
    };

    useGlobalKey("Enter", apply);

    const garrisonCount = reportData?.garrison?.length ?? 0;
    const dispatchCount = reportData?.dispatch?.length ?? 0;
    const totalReportItems = garrisonCount + dispatchCount;
    const hasReportContent = totalReportItems > 0;

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className="flex">
                <Template isLoading={isLoading}>
                    <Template.Header>
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            printable={true}
                            headerChange={true}
                            printComponentRef={printComponentRef}
                            reportData={reportData}
                            data={hasReportContent}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                        />
                    </Template.Header>
                    <Template.Body className={`${collapsed ? "p-0!" : ""}`}>
                        <Menu
                            buttonRef={buttonRef}
                            fixedTop={fixedTop}
                            collapsed={collapsed}
                            toggleFilters={toggleFilters}
                            apply={apply}
                            clearSearch={clearSearch}
                        >
                            <Filters filters={filters} setFilters={setFilters} />
                            <AutoDatetime setFilters={setFilters} />
                        </Menu>
                    </Template.Body>
                </Template>
                <Card className={`w-full h-fit mr-2 ${!hasReportContent ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!hasReportContent ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body className={`flex-1 ${!hasReportContent ? "p-[0]" : "px-[20px]! py-[5px]!"}`}>
                        <NotAppliedContent reportData={reportData} />
                        <NoDataContent reportData={reportData} data={totalReportItems} />
                        <Content
                            reportData={reportData}
                            data={hasReportContent}
                            entity={headerSelected}
                            title={title}
                            setIsLoading={setIsLoading}
                            printComponentRef={printComponentRef}
                        />
                    </Card.Body>
                </Card>
            </div>
        </SubScroll>
    )
}